import { addDays, daysBetween, getMonthRange, parseDate, toUtcMidnight } from './dates';

// ---------------------------------------------------------------------------
// Logika kosztowa (składowanie + THC).
//
// Wyciągnięta ze StockManagement.js do osobnego modułu, bo to jedyna część
// aplikacji, w której błąd kosztuje pieniądze — a była wpleciona pomiędzy JSX
// formularzy i definicje kolumn Excela. Zasady liczenia zostały zachowane
// dokładnie takie, jakie były; zmieniło się tylko miejsce, w którym mieszkają,
// oraz to, że interfejs też może z nich teraz korzystać (koszt "na żywo"),
// zamiast pokazywać go dopiero w wygenerowanym pliku.
//
// Wszystkie obliczenia operują na dniach kalendarzowych znormalizowanych do
// północy UTC — patrz komentarz przy toUtcMidnight() w dates.js.
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/** Bezpieczny dostęp do stawek — stock bez pola `rates` nie może wysypać raportu. */
const ratesOf = (stock) => stock?.rates || {};

/**
 * Stawka dzienna zależna od rozmiaru kontenera.
 * Gdy rozmiaru nie da się rozpoznać z pola containerType, stosowana jest
 * stawka 40' (zachowanie z oryginalnej implementacji).
 */
export const getStorageDailyRate = (container, stock) => {
  const rates = ratesOf(stock);
  const type = (container.containerType || '').toUpperCase();
  if (type.includes('20')) return rates.storageFee20 || 0;
  return rates.storageFee40 || 0;
};

/**
 * ID kontenerów objętych limitem "Free Containers" — pierwsze N kontenerów
 * danego stocku chronologicznie wg Gate-In Date jest trwale zwolnione z opłaty
 * za składowanie, niezależnie od miesiąca raportu.
 */
export const computeExemptContainerIds = (containersForStock, freeContainersQuota) => {
  if (!freeContainersQuota || freeContainersQuota <= 0) return new Set();

  const sorted = [...containersForStock]
    .filter((container) => container.gateInDate)
    .sort((a, b) => new Date(a.gateInDate) - new Date(b.gateInDate));

  return new Set(sorted.slice(0, freeContainersQuota).map((container) => container.id));
};

/**
 * Liczba PŁATNYCH dni składowania wypadających w danym miesiącu.
 *
 * Zasady (bez zmian względem pierwotnej wersji):
 *  - dni darmowe (freeDays) liczone od Gate-In,
 *  - dzień Gate-Out JEST płatny — kontener zajmował plac przez cały ten dzień,
 *  - kontener wciąż ON STOCK liczony jest włącznie do dziś.
 *
 * `dateError: true` oznacza, że pole daty istniało, ale nie dało się go
 * rozpoznać. Dni wynoszą wtedy 0, ale kontener NIE może zostać po cichu
 * pominięty w raporcie — patrz buildBillingReport().
 */
export const calculatePaidStorageDaysInMonth = (container, freeDays, monthString) => {
  if (!container.gateInDate) return { days: 0, dateError: false };

  const gateIn = toUtcMidnight(container.gateInDate);
  if (!gateIn) return { days: 0, dateError: true };

  const { start: monthStart, end: monthEnd } = getMonthRange(monthString);
  const paidStart = addDays(gateIn, freeDays || 0);

  let paidEndExclusive;
  if (container.gateOutDate) {
    const gateOut = toUtcMidnight(container.gateOutDate);
    if (!gateOut) return { days: 0, dateError: true };
    paidEndExclusive = addDays(gateOut, 1);
  } else {
    paidEndExclusive = addDays(toUtcMidnight(new Date()), 1);
  }

  const intersectionStart = paidStart > monthStart ? paidStart : monthStart;
  const intersectionEnd = paidEndExclusive < monthEnd ? paidEndExclusive : monthEnd;

  const days = Math.floor((intersectionEnd.getTime() - intersectionStart.getTime()) / DAY_MS);
  return { days: days > 0 ? days : 0, dateError: false };
};

/**
 * Stan składowania kontenera "na teraz" — na potrzeby interfejsu, nie raportu.
 *
 * To jest nowa funkcja: dotychczas koszt kontenera dało się poznać wyłącznie
 * generując plik Excel po zakończeniu miesiąca. Tutaj liczymy narastająco od
 * Gate-In do dziś (lub do Gate-Out), żeby operator widział na liście, ile dana
 * jednostka już nabiła i któremu kontenerowi zaraz kończy się okres bezpłatny.
 *
 * Zwracany `status`:
 *  - 'pending'  — brak Gate-In, kontener jeszcze nie wjechał
 *  - 'exempt'   — objęty limitem darmowych kontenerów, nie nalicza się nic
 *  - 'free'     — w okresie bezpłatnym
 *  - 'ending'   — w okresie bezpłatnym, ale zostały <= 3 dni (podstawa alertu)
 *  - 'billing'  — nalicza opłatę
 *  - 'closed'   — kontener wyjechał, wartość jest już ostateczna
 *  - 'error'    — daty nie dało się rozpoznać
 */
export const FREE_DAYS_WARNING_THRESHOLD = 3;

export const calculateLiveStorage = (container, stock, { isExempt = false, now = new Date() } = {}) => {
  const rates = ratesOf(stock);
  const currency = rates.currency || 'PLN';
  const freeDays = rates.freeDays || 0;
  const dailyRate = getStorageDailyRate(container, stock);

  const empty = {
    status: 'pending',
    daysOnStock: 0,
    freeDays,
    freeDaysRemaining: freeDays,
    paidDays: 0,
    dailyRate,
    storageCost: 0,
    currency,
  };

  if (!container.gateInDate) return empty;

  const gateIn = toUtcMidnight(container.gateInDate);
  if (!gateIn) return { ...empty, status: 'error' };

  const isClosed = Boolean(container.gateOutDate);
  const endDate = isClosed ? toUtcMidnight(container.gateOutDate) : toUtcMidnight(now);
  if (!endDate) return { ...empty, status: 'error' };

  // +1, bo zarówno dzień wjazdu, jak i dzień bieżący/wyjazdu liczą się w całości.
  const daysOnStock = Math.max(0, daysBetween(gateIn, endDate) + 1);
  const paidDays = Math.max(0, daysOnStock - freeDays);
  const freeDaysRemaining = Math.max(0, freeDays - daysOnStock);
  const storageCost = isExempt ? 0 : paidDays * dailyRate;

  let status;
  if (isExempt) status = 'exempt';
  else if (isClosed) status = 'closed';
  else if (paidDays > 0) status = 'billing';
  else if (freeDaysRemaining <= FREE_DAYS_WARNING_THRESHOLD) status = 'ending';
  else status = 'free';

  return {
    status,
    daysOnStock,
    freeDays,
    freeDaysRemaining,
    paidDays,
    dailyRate,
    storageCost,
    currency,
    isExempt,
  };
};

/**
 * Buduje wiersze raportu kosztowego za wybrany miesiąc.
 *
 * Wydzielone z handlera przycisku, żeby dało się to policzyć (i przetestować)
 * bez dotykania ExcelJS — generowanie pliku jest osobnym krokiem i osobnym,
 * ładowanym dynamicznie modułem.
 *
 * @returns {{rows: Array, grandTotal: number, currencyLabel: string|null,
 *            hasRows: boolean, dateErrorCount: number}}
 */
export const DATE_ERROR_NOTE =
  'BŁĄD: nierozpoznany format daty Gate-In/Gate-Out — koszt składowania NIE wyliczony, sprawdź ręcznie!';

/**
 * Pozycje kosztowe JEDNEGO stocku za dany miesiąc.
 *
 * Wydzielone z buildBillingReport, bo z tych samych obliczeń korzystają teraz
 * dwa dokumenty: zbiorczy raport XLSX i nota obciążeniowa PDF wystawiana
 * klientowi. Liczenie ich osobno oznaczałoby, że arkusz i nota mogą kiedyś
 * pokazać różne kwoty za ten sam miesiąc.
 *
 * @returns {{items: Array, total: number, currency: string, dateErrorCount: number}}
 */
export const buildStockBilling = ({ stock, containers, monthString }) => {
  const rates = ratesOf(stock);
  const freeDays = rates.freeDays || 0;
  const containersForStock = containers.filter((c) => c.associatedStockId === stock.id);
  const exemptIds = computeExemptContainerIds(containersForStock, rates.freeContainers || 0);

  const { start, end } = getMonthRange(monthString);
  const fallsInMonth = (date) => {
    if (!date) return false;
    const normalized = toUtcMidnight(date);
    return normalized >= start && normalized < end;
  };

  const items = [];
  let total = 0;
  let dateErrorCount = 0;

  containersForStock.forEach((container) => {
    const { days: paidStorageDays, dateError } = calculatePaidStorageDaysInMonth(
      container,
      freeDays,
      monthString
    );

    const isExempt = exemptIds.has(container.id);
    const dailyRate = getStorageDailyRate(container, stock);
    const storageCost = isExempt ? 0 : paidStorageDays * dailyRate;

    const gateInDate = parseDate(container.gateInDate);
    const gateOutDate = parseDate(container.gateOutDate);

    const thcInApplies = fallsInMonth(gateInDate);
    const thcOutApplies = fallsInMonth(gateOutDate);
    const thcGateInCost = thcInApplies ? rates.thcGateIn || 0 : 0;
    const thcGateOutCost = thcOutApplies ? rates.thcGateOut || 0 : 0;
    const totalCost = storageCost + thcGateInCost + thcGateOutCost;

    // Kontener trafia do zestawienia, jeśli generuje koszt lub ruch w tym
    // miesiącu, ALBO ma błędną datę — taki przypadek musi być widoczny,
    // a nie po cichu pominięty.
    const isRelevant = paidStorageDays > 0 || thcInApplies || thcOutApplies || dateError;
    if (!isRelevant) return;

    if (dateError) dateErrorCount += 1;

    items.push({
      containerNumber: container.containerNumber || '',
      containerType: container.containerType || '',
      gateInDate: gateInDate || '',
      gateOutDate: gateOutDate || '',
      isExempt,
      paidStorageDays,
      dailyRate,
      storageCost,
      thcGateInCost,
      thcGateOutCost,
      totalCost,
      dateError,
    });

    total += totalCost;
  });

  return { items, total, currency: rates.currency || 'PLN', dateErrorCount };
};

export const buildBillingReport = ({ stocks, containers, monthString }) => {
  // Sumę całkowitą można policzyć tylko wtedy, gdy wszystkie stocki w raporcie
  // rozliczają się w tej samej walucie — inaczej sumowalibyśmy złotówki z euro.
  const distinctCurrencies = [...new Set(stocks.map((stock) => ratesOf(stock).currency))];
  const currencyLabel = distinctCurrencies.length === 1 ? distinctCurrencies[0] : null;

  const rows = [];
  let grandTotal = 0;
  let hasRows = false;
  let dateErrorCount = 0;

  stocks.forEach((stock) => {
    const billing = buildStockBilling({ stock, containers, monthString });
    if (billing.items.length === 0) return;

    hasRows = true;
    dateErrorCount += billing.dateErrorCount;

    billing.items.forEach((item) => {
      rows.push({
        stockName: stock.stockName,
        containerNumber: item.containerNumber,
        containerType: item.containerType,
        gateInDate: item.gateInDate,
        gateOutDate: item.gateOutDate,
        isExempt: item.isExempt ? 'TAK (free quota)' : '',
        paidStorageDays: item.paidStorageDays,
        dailyRate: item.dailyRate,
        storageCost: item.storageCost,
        thcGateInCost: item.thcGateInCost,
        thcGateOutCost: item.thcGateOutCost,
        totalCost: item.totalCost,
        flagNote: item.dateError ? DATE_ERROR_NOTE : '',
      });
    });

    rows.push({
      stockName: `SUMA: ${stock.stockName} (${billing.currency})`,
      totalCost: billing.total,
      __rowStyle: 'subtotal',
    });

    grandTotal += billing.total;
  });

  if (hasRows && stocks.length > 1) {
    rows.push({
      stockName: currencyLabel
        ? `SUMA CAŁKOWITA (${currencyLabel})`
        : 'SUMA CAŁKOWITA NIEDOSTĘPNA (stocki w różnych walutach — patrz sumy per stock)',
      totalCost: currencyLabel ? grandTotal : '',
      __rowStyle: 'total',
    });
  }

  return { rows, grandTotal, currencyLabel, hasRows, dateErrorCount };
};
